import React, { useState } from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { API_URL } from '../utils/api';

const DeleteButton = ({ 
  itemId, 
  itemType, 
  onSuccess, 
  title = 'Delete Item',
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  size = 'medium',
  color = 'error',
  photoUrl = null
}) => {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleOpen = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError('');
  };

  const getEndpoint = () => {
    switch (itemType.toLowerCase()) {
      case 'memory':
        return `${API_URL}/api/memories/${itemId}`;
      case 'event':
        return `${API_URL}/api/events/${itemId}`;
      case 'match':
        return `${API_URL}/api/matches/${itemId}`;
      case 'chat':
        return `${API_URL}/api/chat/${itemId}`;
      default:
        return `${API_URL}/api/${itemType}s/${itemId}`;
    }
  };

  const extractCloudinaryPublicId = (url) => {
    if (!url || typeof url !== 'string') return null;
    
    // Check if it's a Cloudinary URL
    if (!url.includes('cloudinary.com')) return null;
    
    try {
      // Extract the public ID from a typical Cloudinary URL
      // Example: https://res.cloudinary.com/demo/image/upload/v1234567890/some-folder/image_publicid.jpg
      const urlParts = url.split('/');
      const uploadIndex = urlParts.findIndex(part => part === 'upload');
      
      if (uploadIndex === -1) return null;
      
      // Join all parts after 'upload' except the version segment (starts with v)
      const publicIdParts = urlParts.slice(uploadIndex + 1).filter(part => !part.startsWith('v'));
      const publicId = publicIdParts.join('/').split('.')[0]; // Remove file extension
      
      return publicId;
    } catch (error) {
      console.error('Error extracting Cloudinary public ID:', error);
      return null;
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');
    
    try {
      const endpoint = getEndpoint();
      console.log('Deleting:', itemType, 'with ID:', itemId, 'using endpoint:', endpoint);
      const token = localStorage.getItem('token');
      
      // If we have a photoUrl and it's from Cloudinary, we need to delete the image too
      const cloudinaryPublicId = photoUrl ? extractCloudinaryPublicId(photoUrl) : null;
      
      // First delete the item from our database
      await axios.delete(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        data: {
          cloudinaryPublicId: cloudinaryPublicId
        }
      });
      
      setIsDeleting(false);
      handleClose();
      
      if (onSuccess) {
        onSuccess(itemId);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      console.error('Item type:', itemType, 'Item ID:', itemId, 'Endpoint:', getEndpoint());
      setIsDeleting(false);
      
      // Try to get a more specific error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete. Please try again later.';
      
      setError(errorMessage);
    }
  };

  return (
    <>
      <IconButton 
        onClick={handleOpen}
        size={size}
        color={color}
        sx={{
          '&:hover': {
            transform: 'scale(1.1)',
          },
          transition: 'transform 0.2s'
        }}
      >
        <DeleteIcon />
      </IconButton>
      
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 'var(--radius-md)',
            padding: '8px',
            maxWidth: '400px',
            boxShadow: 'var(--elevation-3)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {title}
        </DialogTitle>
        
        <DialogContent>
          <DialogContentText>
            {message}
          </DialogContentText>
          {error && (
            <DialogContentText sx={{ color: 'error.main', mt: 2 }}>
              {error}
            </DialogContentText>
          )}
        </DialogContent>
        
        <DialogActions sx={{ padding: '16px' }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
            disabled={isDeleting}
            sx={{ 
              borderRadius: 'var(--radius-sm)',
              textTransform: 'none',
              minWidth: '100px'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
            sx={{ 
              borderRadius: 'var(--radius-sm)',
              textTransform: 'none',
              minWidth: '100px'
            }}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteButton; 